import React, { useMemo } from 'react';
import { useAIInsight } from '../state/data/aiInsight';

/**
 * AI Insight Panel — short Claude-powered briefing (2–3 sentences) that
 * replaces the prior on-device DailyHighlights / PatientHighlight summary.
 *
 * Auto-fetches on mount; small refresh button to re-run.
 */
function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Scan summary text for patient display names ("FirstName L.") and wrap
 * matches in clickable spans that call onSelectPatient(userid).
 */
function linkifyPatientNames(text, allPatients, onSelectPatient) {
  if (!text || !allPatients?.length || !onSelectPatient) return text;

  // Build display-name → userid map
  const nameMap = new Map();
  for (const p of allPatients) {
    const first = (p.firstname || '').trim();
    const last = (p.lastname || '').trim();
    if (!first) continue;
    // "FirstName L." pattern the AI uses
    const display = last ? `${first} ${last[0]}.` : first;
    nameMap.set(display, String(p.userid));
  }
  if (nameMap.size === 0) return text;

  // Build regex — longest names first to avoid partial matches
  const names = [...nameMap.keys()].sort((a, b) => b.length - a.length);
  const escaped = names.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(${escaped.join('|')})`, 'g');

  const parts = text.split(re);
  if (parts.length === 1) return text; // no matches

  return parts.map((part, i) => {
    const uid = nameMap.get(part);
    if (uid) {
      return (
        <span
          key={i}
          role="button"
          tabIndex={0}
          style={patientLinkStyle}
          onClick={() => onSelectPatient(uid)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onSelectPatient(uid); }}
        >
          {part}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

export default function AIInsightPanel({ mode, role, withingsUserId = null, userName = null, patientName = null, allPatients = null, onSelectPatient = null }) {
  const input = useMemo(() => ({ mode, role, withingsUserId }), [mode, role, withingsUserId]);
  const query = useAIInsight(input);

  const res = query.data;
  const summary = res?.status === 'success' ? res.insight?.summary ?? null : null;

  // No usable briefing (loading, request failed, or unavailable) → render nothing, no error chrome.
  if (!summary) return null;

  const fetchInsight = () => query.refetch();
  const refreshing = query.isFetching;
  const firstName = (userName || '').trim().split(/\s+/)[0];
  const title = mode === 'patient'
    ? (patientName ? `Briefing on ${patientName}` : 'Patient briefing')
    : `${greeting()}${firstName ? `, ${firstName}` : ''}`;

  return (
    <section className="ai-insight-panel" style={panelStyle} aria-label="AI briefing">
      <div style={rowStyle}>
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }}>
          <defs>
            <linearGradient id="aiSparkleGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#8b5cf6" />
              <stop offset="100%" stopColor="#ec4899" />
            </linearGradient>
          </defs>
          <path
            fill="url(#aiSparkleGrad)"
            d="M12 2 L13.5 9 L20.5 10.5 L13.5 12 L12 19 L10.5 12 L3.5 10.5 L10.5 9 Z M19 16 L19.7 18.3 L22 19 L19.7 19.7 L19 22 L18.3 19.7 L16 19 L18.3 18.3 Z"
          />
        </svg>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle}>{title}</div>
          <div style={textStyle}>{linkifyPatientNames(summary, allPatients, onSelectPatient)}</div>
          <div style={footerStyle}>Withings AI · verify clinical actions in patient records</div>
        </div>
        <button
          type="button"
          onClick={fetchInsight}
          disabled={refreshing}
          style={refreshStyle(refreshing)}
          title="Refresh briefing"
          aria-label="Refresh AI briefing"
        >
          ↻
        </button>
      </div>
    </section>
  );
}

const panelStyle = {
  borderRadius: 14,
  padding: '14px 16px',
  margin: '12px 0 24px',
  background: 'var(--wi-background-secondary, #fafbff)',
  // Gradient border via double background trick — works on light + dark themes
  border: '1.5px solid transparent',
  backgroundImage:
    'linear-gradient(var(--wi-background-secondary, #fafbff), var(--wi-background-secondary, #fafbff)), ' +
    'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box',
};
const rowStyle = { display: 'flex', alignItems: 'flex-start', gap: 10 };
const titleStyle = {
  fontSize: 15, fontWeight: 600, color: 'var(--wi-text-primary, #0f172a)',
  marginBottom: 4,
};
const textStyle = { fontSize: 14, lineHeight: 1.5, color: 'var(--wi-text-primary, #0f172a)' };
const refreshStyle = (disabled) => ({
  background: 'transparent', border: '1px solid var(--wi-divider, #e5e7eb)',
  borderRadius: 6, padding: '2px 8px', fontSize: 14,
  color: 'var(--wi-text-secondary, #64748b)',
  cursor: disabled ? 'wait' : 'pointer', flexShrink: 0,
});
const patientLinkStyle = {
  color: 'var(--wi-status-info, #2563eb)',
  cursor: 'pointer',
  fontWeight: 600,
  textDecoration: 'underline',
  textDecorationStyle: 'dotted',
  textUnderlineOffset: 2,
};
const footerStyle = {
  marginTop: 8, fontSize: 10, color: 'var(--wi-text-secondary, #94a3b8)',
  textTransform: 'uppercase', letterSpacing: 0.5,
};
